import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { addSchoolTeacher } from '../../../../actions/user.actions';
import { TeacherAdded } from '../../../../ampli';
import { getProductByLicenceModelId, schoolLicences } from '../../../../constants';
import { selectSelectedSchoolId, selectUser } from '../../../../reducers/user.selectors';
import services from '../../../../services';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import useAction from '../../../../utils/useAction';
import { useRequestMoreSeats } from '../../hooks/useRequestMoreSeats';

interface AddTeacherProps {
	onTeacherAdded: () => void;
	open: boolean;
	onClose: () => void;
	schoolSeats: Record<string, { remaining: number }>;
}

const AddSchoolTeacher: React.FC<AddTeacherProps> = ({
	onTeacherAdded,
	open,
	onClose,
	schoolSeats,
}) => {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		licenceModelId: '',
	});

	const [loading, setLoading] = useState(false);
	const addSchoolTeacherAction = useAction(addSchoolTeacher);
	const { t } = useTranslation();
	const { openSnackbar } = useContext(SnackbarContext);
	const user = useSelector(selectUser);
	const selectedSchoolId = useSelector(selectSelectedSchoolId);
	const { handleRequestMoreSeats } = useRequestMoreSeats();

	const sendTeacherAddedEvent = () => {
		if (user && user.isSchoolAdmin && schoolSeats && selectedSchoolId) {
			const userProduct =
				getProductByLicenceModelId(Number.parseInt(formData.licenceModelId))?.ampliName ??
				schoolLicences.models[0].ampliName;

			const subscriptionProducts: string[] = [userProduct];
			services.track.ampliEventTrack({
				event: new TeacherAdded({
					'school id': selectedSchoolId,
					'subscription products': subscriptionProducts,
				}),
			});
		}
	};

	const showError = (message: string) => {
		openSnackbar({
			type: 'error',
			message,
		});
	};

	const showInfo = (message: string) => {
		openSnackbar({
			type: 'success',
			message,
		});
	};

	const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({
			...prev,
			[field]: event.target.value,
		}));
	};

	const validateEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	};

	const clearForm = () => {
		setFormData({
			firstName: '',
			lastName: '',
			email: '',
			licenceModelId: '',
		});
	};

	const handleClose = () => {
		setLoading(false);
		clearForm();
		onClose();
	};

	const handleSubmit = async () => {
		// Validate required fields
		if (!formData.firstName || !formData.lastName || !formData.email || !formData.licenceModelId) {
			showError(t('All fields are required'));
			return;
		}

		// Validate email format
		if (!validateEmail(formData.email)) {
			showError(t('Invalid email address'));
			return;
		}
		setLoading(true);

		try {
			if (!selectedSchoolId) return;
			const result = await addSchoolTeacherAction(
				selectedSchoolId,
				formData.firstName,
				formData.lastName,
				formData.email,
				Number.parseInt(formData.licenceModelId)
			);
			if (!result.error) {
				showInfo(t('Teacher added successfully'));
				sendTeacherAddedEvent();
			} else {
				let message: string;
				if (result.error.data.message.indexOf('No available seats for') > -1)
					message = t('No available seats for <productName>', {
						productName: result.error.data.message.replace('No available seats for ', ''),
					});
				else message = t(result.error.data.message) || t('Failed to add teacher');
				showError(message);
				return;
			}

			// Clear form and notify parent
			clearForm();
			onTeacherAdded();
		} catch (err: any) {
			showError(t(err.message) || t('Failed to add teacher'));
		} finally {
			setLoading(false);
		}
	};

	const handleCloseDialogAndRequestMoreSeats = () => {
		handleRequestMoreSeats();
		onClose();
	};

	const availableModels = schoolLicences.models.filter((model) => {
		return model && schoolSeats?.[model.zuoraLabel + 'Seats']?.remaining > 0;
	});
	const hasAvailableSeats = availableModels && availableModels.length > 0;

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{t('Add Teacher')}</DialogTitle>
			<DialogContent>
				{hasAvailableSeats ? (
					<>
						<TextField
							fullWidth
							size="small"
							label={t('Vorname')}
							value={formData.firstName}
							onChange={handleInputChange('firstName')}
							variant="standard"
							required
							inputProps={{ 'aria-label': 'Vorname' }}
						/>
						<TextField
							fullWidth
							size="small"
							label={t('Nachname')}
							value={formData.lastName}
							onChange={handleInputChange('lastName')}
							variant="standard"
							required
							inputProps={{ 'aria-label': 'Nachname' }}
						/>
						<TextField
							fullWidth
							size="small"
							label={t('E-Mail-Adresse')}
							value={formData.email}
							onChange={handleInputChange('email')}
							variant="standard"
							required
							inputProps={{ 'aria-label': 'E-Mail-Adresse' }}
						/>
						<TextField
							select
							fullWidth
							size="small"
							label={t('Product (Produkt)')}
							value={formData.licenceModelId}
							onChange={handleInputChange('licenceModelId')}
							variant="standard"
							required
							inputProps={{ 'aria-label': 'Product' }}
							disabled={!hasAvailableSeats}
						>
							{availableModels.map((model) => (
								<MenuItem key={model.id} value={model.id}>
									{model.shortName}
								</MenuItem>
							))}
						</TextField>
					</>
				) : (
					<Typography color="textSecondary" sx={{ mt: 2, mb: 2 }}>
						{t('All seats are taken, you can remove persons from the license or request more seats')}
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{t('Back')}</Button>
				{hasAvailableSeats ? (
					<Button
						onClick={handleSubmit}
						startIcon={loading && <CircularProgress size={20} color="inherit" />}
						disabled={loading}
						variant="contained"
						color="success"
					>
						{t('Submit')}
					</Button>
				) : (
					<Button variant="contained" color="success" onClick={handleCloseDialogAndRequestMoreSeats}>
						{t('Request More Seats')}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AddSchoolTeacher;
